import {
  Component,
  OnInit,
  ChangeDetectorRef,
  AfterViewInit,
  ViewChild,
  Signal,
  inject,
  HostListener,
  Input
} from '@angular/core';
import {map, tap} from 'rxjs';
import {ActivatedRoute, Router, NavigationEnd} from '@angular/router';
import {SearchService} from 'src/app/services/search/search.service';
import {SpecialRoutingService} from 'src/app/services/special-routing/special-routing.service';
import {RxAuthService} from 'src/app/services/rx-auth/rx-auth.service';
import {faThemeisle} from '@fortawesome/free-brands-svg-icons';
import {toSignal} from '@angular/core/rxjs-interop';
import {MediaQueryService} from 'src/app/services/media-query/media-query.service';
import {SearchComponent} from "../../shared/toolbox/search/search.component";
import {ExplorerCommonFunctions} from "./explorerCommonFunctions";
import {filterOptions, SelectedFilters} from "./explorerInterfaces";
import {TabularDataDownloadService} from "../../services/tabular-data-download/tabular-data-download.service";
import {lastValueFrom} from 'rxjs';

@Component({
  selector: 'app-explorer',
  templateUrl: './explorer.component.html',
  styleUrls: ['./explorer.component.scss'],
})
export class ExplorerComponent implements OnInit, AfterViewInit {

  //IsChild will change component behaviour: hide search bar and alphabetic search, and show only results for a specific query
  @Input() isChild: boolean = false;
  @Input() preselectedFilters: SelectedFilters | undefined = undefined
  @Input() preselectedQuery: string = ''
  @Input() preselectedView: string = 'all'
  @Input() blockFilterButton: string[] = [] //Expects an array with any filter name.

  countryCode: string = '';


  query: string = '';
  searchValue: string = '';
  selectedView: string = 'all';
  selectedLetter: string = 'ALL';
  resultsIndex: number = 0;
  totalHits: number = 0;
  lastState: 'above' | 'below' | null = null;
  private previousUrl: string | null = null;


  public isOpened: boolean = false;
  showData: boolean = false;
  showInnerLoader: boolean = true;
  infiniteScrollLoader: boolean = false;
  endOfResultsTemplate: boolean = false;
  dropdownVisible = false;
  regionDropdownVisible: boolean = false;
  clusterDropdownVisible: boolean = false;
  sigMarketsDropdownVisible: boolean = false;
  hqDropdownVisible: boolean = false;
  sectorDropdownVisible: boolean = false;
  rankingDropdownVisible: boolean = false;
  companyTypeDropdownVisible: boolean = false;
  businessTypeDropdownVisible: boolean = false;
  supplierCategoryDropdownVisible: boolean = false;
  supplierDropdownVisible: boolean = false;
  productDropdownVisible: boolean = false;
  showDownloadDialog: boolean = false;
  showSupplierDownloadDialog: boolean = false;
  loadingRetailersDownload = {value: false};
  loadingSuppliersDownload = {value: false};
  onStart: boolean = false;

  listsSearchResults: any[] = [];
  retailersSearchResults: any[] = [];
  vendorsSearchResults: any[] = [];
  selectedRegions: any[] = [];
  selectedClusters: any[] = [];
  selectedSigMarkets: any[] = [];
  selectedHqs: any[] = [];
  selectedSectors: any[] = [];
  selectedRankings: any[] = [];
  selectedCompanyTypes: any[] = [];
  selectedBusinessTypes: any[] = [];
  selectedSupplierCategories: any[] = [];
  selectedSuppliers: any[] = [];
  selectedProducts: any[] = [];
  availableEchelons: any[] = []
  rclIDs: any[] = [];


  selectedFilters: SelectedFilters | null = {
    region: [],
    cluster: [],
    sigMarket: [],
    hq: [],
    sector: [],
    ranking: [],
    companyType: [],
    businessType: [],
    supplierCategory: [],
    supplier: [],
    product: [],

  };
  paramSelectedFilters: SelectedFilters | null = {
    region: [],
    cluster: [],
    sigMarket: [],
    hq: [],
    sector: [],
    ranking: [],
    companyType: [],
    businessType: [],
    supplierCategory: [],
    supplier: [],
    product: [],
  };
  allFilterOptions: filterOptions = {
    company_types: [],
    business_types: [],
    sectors: [],
    headquarters: [],
    rankings: [],
    clusters: [],
    ranking_regions: [],
    significant_markets: [],
    supplier_categories: [],
    suppliers: [],
    products: [],
  }

  QUERY_PARAM_TO_FILTER_KEY: { [key: string]: keyof SelectedFilters } = {
    sector: 'sector',
    headquarter: 'hq',
    cluster: 'cluster',
    companyType: 'companyType',
    businessType: 'businessType',
    supplierCategory: 'supplierCategory',
    supplier: 'supplier',
    product: 'product',
    significant_market: 'sigMarket',
    list_region: 'region',
    ranking: 'ranking',
  };

  // Separate ViewChild decorators for each paginator
  @ViewChild('searchBar', {static: false}) searchComponent!: SearchComponent;


  private mediaQueryService: MediaQueryService = inject(MediaQueryService);
  public isMobile: Signal<boolean> = toSignal(
    this.mediaQueryService.getDisplayType().pipe(
      map(({isMobile}) => isMobile),
      tap((isMobile) => (this.isOpened = isMobile && this.isOpened)) // ensures that if you resize the window to desktop, the search bar will be closed
    ),
    {initialValue: window.innerWidth < 768}
  );

  constructor(
    private route: ActivatedRoute,
    private searchService: SearchService,
    public specialRoutingService: SpecialRoutingService,
    private cdr: ChangeDetectorRef, // Inject ChangeDetectorRef
    public auth: RxAuthService,
    private router: Router,
    public explorerCommonFunctions: ExplorerCommonFunctions,
    private tabularDataDownloadService: TabularDataDownloadService,
  ) {
  }

  // INIT START -----
  async ngOnInit(): Promise<void> {
    this.onStart = true;
    await this.getFilterOptionsData();
    if (this.isChild) {
      this.selectedView = this.preselectedView!
      this.query = this.preselectedQuery!

      Object.entries(this.preselectedFilters!).forEach(([key, value]) => {
        for (let singular_value of value) {
          this.toggleFilterSelection(key, singular_value)
        }

      });
      this.search(this.query, this.resultsIndex, this.updateSelectedFilters)

    } else {
      this.route.queryParams.subscribe((params) => {
        this.query = decodeURIComponent(params['search']) || ''; // Get the 'search' query parameter from the URL
        if (this.query.length == 1) {
          const isAlphabetic = /^[A-Za-z]$/.test(this.query);

          if (isAlphabetic) {
            this.selectedLetter = this.query.toUpperCase(); // Set to the letter if it's A-Z
          } else {
            this.selectedLetter = '#'; // Set to '#' if it's not A-Z
          }
        } else {
          this.selectedLetter = 'ALL';
        }
        this.search(this.query, this.resultsIndex, this.updateSelectedFilters);
        // }
        if (params['view']) {
          this.selectedView = params['view'];
        }
      });
    }

  }

  ngAfterViewInit() {
    // Optionally, populate the search input from URL when component loads
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        const currentUrl = event.urlAfterRedirects;

        if (this.previousUrl === currentUrl) {
          const hasActiveFilters = this.selectedFilters
            ? Object.values(this.selectedFilters).some(
              (value: any) => Array.isArray(value) && value.length > 0
            )
            : false;
          this.dropdownVisible = hasActiveFilters;
          // Put your custom logic here
        }

        this.previousUrl = currentUrl;
      }
    });
    this.route.queryParams.subscribe(params => {
      if (params['query']) {
        this.searchComponent.inputValue = params['query']; // Set search input if query param exists
      }    else  if (params['search']) {
        this.searchComponent.inputValue = params['search']; // Set search input if query param exists
      }
    });
    this.onStart = false;
  }

  getFilterQueryParams(): void {
    if (!this.paramSelectedFilters) return; // Ensure it's defined before using

    this.route.queryParamMap.subscribe(params => {
      // Reset all filters
      Object.keys(this.paramSelectedFilters!).forEach(key => {
        this.paramSelectedFilters![key as keyof SelectedFilters] = [];
      });

      // Populate filters from query params
      params.keys.forEach(paramKey => {
        const value = params.get(paramKey);
        if (!value) return;

        if (paramKey === 'view') {
          (this.paramSelectedFilters! as any)['view'] = value;
          return;
        }
        if (paramKey === 'search') {
          (this.paramSelectedFilters! as any)['query'] = value;
          return;
        }

        const filterKey = this.QUERY_PARAM_TO_FILTER_KEY[paramKey];
        if (filterKey) {
          this.paramSelectedFilters![filterKey as keyof SelectedFilters] = value.split(',').map(v => {
            const num = Number(v);
            return isNaN(num) ? decodeURIComponent(v) : num;
          });
        }
      });

      if (params.get('view')) {
        this.selectedView = params.get('view') as string;
      }
      if (params.get('search')) {
        this.searchValue = params.get('search') as string;
      }

    });

    this.initializeSelectedFiltersFromParams();
  }

  initializeSelectedFiltersFromParams(): void {
    if (!this.paramSelectedFilters) return;

    const optionMap: Record<keyof SelectedFilters, any[]> = {
      region: this.allFilterOptions.ranking_regions ?? [],
      cluster: this.allFilterOptions.clusters ?? [],
      sigMarket: this.allFilterOptions.significant_markets ?? [],
      hq: this.allFilterOptions.headquarters ?? [],
      sector: this.allFilterOptions.sectors ?? [],
      ranking: this.allFilterOptions.rankings ?? [],
      companyType: this.allFilterOptions.company_types ?? [],
      businessType: this.allFilterOptions.business_types ?? [],
      supplierCategory: this.allFilterOptions.supplier_categories ?? [],
      supplier: this.allFilterOptions.suppliers ?? [],
      product: this.allFilterOptions.products ?? [],
    };

    const keyToClassPropMap: Partial<Record<keyof SelectedFilters, string>> = {
      region: 'selectedRegions',
      cluster: 'selectedClusters',
      sigMarket: 'selectedSigMarkets',
      hq: 'selectedHqs',
      sector: 'selectedSectors',
      ranking: 'selectedRankings',
      companyType: 'selectedCompanyTypes',
      businessType: 'selectedBusinessTypes',
      supplierCategory: 'selectedSupplierCategories',
      supplier: 'selectedSuppliers',
      product: 'selectedProducts',
    };

    const selectedFiltersArray: Array<{ name: string; values: string[] }> = [];

    (Object.keys(this.paramSelectedFilters) as (keyof SelectedFilters)[]).forEach(key => {
      const selectedValues: (string | number)[] = this.paramSelectedFilters?.[key] ?? [];
      const availableOptions = optionMap[key] ?? [];

      const matchedSelections = availableOptions.filter(option => {
        // If option is string (e.g., "Private")
        if (typeof option === 'string') {
          return selectedValues.some(v => String(v).toLowerCase() === option.toLowerCase());
        }

        // If option is object
        return selectedValues.some(v =>
          String(v).toLowerCase() === String(option.id).toLowerCase() ||
          String(v).toLowerCase() === String(option.name).toLowerCase()
        );
      });

      const matchedIds = matchedSelections.map(option => {
        if (typeof option === 'string') return option;
        return option.id ?? option.name;
      });

      // Update central selectedFilters object
      this.selectedFilters![key] = matchedIds;

      // Update individual class property if mapped
      const classPropName = keyToClassPropMap[key];
      if (classPropName && classPropName in this) {
        (this as any)[classPropName] = matchedSelections;
      }

      selectedFiltersArray.push({
        name: key,
        values: matchedIds.map(v => String(v)),
      });
    });


    this.updateSelectedFilters;
    this.applyFilters();
  }
  // INIT END -----


  // LISTENERS START -----
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const clickedInside = (event.target as HTMLElement).closest('.checkbox-dropdown');
    if (!clickedInside) {
      this.closeAllDropdowns()
    }
  }

  @HostListener('window:scroll', ['$event'])
  onWindowScroll(event: any) {
    if (this.selectedView !== 'all') {
      const specialElement = document.querySelector('.full-page-container');

      if (specialElement) {
        const rect = specialElement.getBoundingClientRect();
        const windowHeight = window.innerHeight;

        // Calculate the threshold
        const threshold = windowHeight + (windowHeight / 8);

        if (rect.bottom <= threshold && this.showData && !this.infiniteScrollLoader) {
          if (this.lastState !== 'above') {
            this.lastState = 'above'; // Update the state
            this.loadMoreItems();

          }
        } else if (rect.bottom > threshold && this.showData && !this.infiniteScrollLoader) {
          if (this.lastState !== 'below') {
            this.lastState = 'below'; // Update the state
          }
        }
      }
    }

  }

  loadMoreItems() {
    this.infiniteScrollLoader = true;
    this.resultsIndex++;
    this.searchService.search(this.query, this.resultsIndex, this.updateSelectedFilters).subscribe(
      (response) => {
        this.infiniteScrollLoader = false;
        if (response.retailer_results.length > 0 && response.retailer_results.length > 0) {
          this.retailersSearchResults.push(...response.retailer_results);
          this.vendorsSearchResults.push(...response.vendor_results);
          this.listsSearchResults.push(...response.list_results);
        } else {
          this.endOfResultsTemplate = true;
        }

        this.cdr.detectChanges(); // Manually trigger change detection
      },
      (error: any) => {
        this.infiniteScrollLoader = false;
        console.error('Error searching RCL', error.message);
      }
    );

  }

  // LISTENERS END -----


  // VIEW/TAB FUNCTIONS START -----
  selectView(view: string) {
    this.selectedView = view;
    this.cdr.detectChanges();
  }

  toggleView(viewString: string): void {
    this.selectedView = viewString;
  }

  // VIEW/TAB FUNCTIONS END -----


  // DROPDOWN FUNCTIONS  START -----
  toggleDropdown() {
    this.dropdownVisible = !this.dropdownVisible;
  }

  closeAllDropdowns() {
    this.explorerCommonFunctions.getAllUniqueFilters(this.allFilterOptions, this.selectedSupplierCategories, this.selectedSuppliers, this.selectedRankings)

    this.regionDropdownVisible = false;
    this.clusterDropdownVisible = false;
    this.sigMarketsDropdownVisible = false;
    this.hqDropdownVisible = false;
    this.sectorDropdownVisible = false;
    this.rankingDropdownVisible = false;
    this.companyTypeDropdownVisible = false;
    this.businessTypeDropdownVisible = false;
    this.supplierCategoryDropdownVisible = false;
    this.supplierDropdownVisible = false;
    this.productDropdownVisible = false;
  }

  toggleFilterDropdown(dropdownName: string) {

    this.closeAllDropdowns();

    if (dropdownName === 'region') {
      this.regionDropdownVisible = !this.regionDropdownVisible;
    } else if (dropdownName === 'cluster') {
      this.clusterDropdownVisible = !this.clusterDropdownVisible;
    } else if (dropdownName === 'sigMarket') {
      this.sigMarketsDropdownVisible = !this.sigMarketsDropdownVisible;
    } else if (dropdownName === 'hq') {
      this.hqDropdownVisible = !this.hqDropdownVisible;
    } else if (dropdownName === 'sector') {
      this.sectorDropdownVisible = !this.sectorDropdownVisible;
    } else if (dropdownName === 'ranking') {
      this.rankingDropdownVisible = !this.rankingDropdownVisible;
    } else if (dropdownName === 'company_type') {
      this.companyTypeDropdownVisible = !this.companyTypeDropdownVisible;
    } else if (dropdownName === 'business_type') {
      this.businessTypeDropdownVisible = !this.businessTypeDropdownVisible;
    } else if (dropdownName === 'supplier_category') {
      this.supplierCategoryDropdownVisible = !this.supplierCategoryDropdownVisible;
    } else if (dropdownName === 'supplier') {
      this.supplierDropdownVisible = !this.supplierDropdownVisible;
    } else if (dropdownName === 'product') {
      this.productDropdownVisible = !this.productDropdownVisible;
    }
  }

  // DROPDOWN FUNCTIONS  END -----


  // FILTER  FUNCTIONS START -----
  getFilterOptionsData(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.searchService.getAllFilterOptions().subscribe(
        (response) => {
          this.allFilterOptions = response.filter_options;
          this.getFilterQueryParams();
          this.initializeSelectedFiltersFromParams();
          resolve(); // Resolve the promise once done
        },
        (error: any) => {
          console.error('Error getting all filter options', error.message);
          reject(error);
        }
      );
    });
  }

  toggleFilterSelection(filterName: string, selection: any): void {
    if (filterName === 'region') {
      if (this.isFilterSelected(filterName, selection)) {
        this.removeSelection(filterName, selection);
      } else {
        this.selectedRegions.push(selection);
      }
    } else if (filterName === 'cluster') {
      if (this.isFilterSelected(filterName, selection)) {
        this.removeSelection(filterName, selection);
      } else {
        this.selectedClusters.push(selection);
      }
    } else if (filterName === 'sigMarket') {
      if (this.isFilterSelected(filterName, selection)) {
        this.removeSelection(filterName, selection);
      } else {
        this.selectedSigMarkets.push(selection);
      }
    } else if (filterName === 'hq') {
      if (this.isFilterSelected(filterName, selection)) {
        this.removeSelection(filterName, selection);
      } else {
        this.selectedHqs.push(selection);
      }
    } else if (filterName === 'sector') {
      if (this.isFilterSelected(filterName, selection)) {
        this.removeSelection(filterName, selection);
      } else {
        this.selectedSectors.push(selection);
      }
    } else if (filterName === 'ranking') {
      if (this.isFilterSelected(filterName, selection)) {
        this.removeSelection(filterName, selection);
      } else {
        this.selectedRankings.push(selection);
      }
    } else if (filterName === 'company_type') {
      if (this.isFilterSelected(filterName, selection)) {
        this.removeSelection(filterName, selection);
      } else {
        this.selectedCompanyTypes.push(selection);
      }
    } else if (filterName === 'business_type') {
      if (this.isFilterSelected(filterName, selection)) {
        this.removeSelection(filterName, selection);
      } else {
        this.selectedBusinessTypes.push(selection);
      }
    } else if (filterName === 'supplier_category') {
      if (this.isFilterSelected(filterName, selection)) {
        this.removeSelection(filterName, selection);
      } else {
        this.selectedSupplierCategories.push(selection);
      }
    } else if (filterName === 'supplier') {
      if (this.isFilterSelected(filterName, selection)) {
        this.removeSelection(filterName, selection);
      } else {
        this.selectedSuppliers.push(selection);
      }
    } else if (filterName === 'product') {
      if (this.isFilterSelected(filterName, selection)) {
        this.removeSelection(filterName, selection);
      } else {
        this.selectedProducts.push(selection);
      }
    }
  }

  isFilterSelected(filterName: string, selection: any): boolean {

    if (filterName === 'region') {
      return this.selectedRegions.includes(selection);
    } else if (filterName === 'cluster') {
      return this.selectedClusters.includes(selection);
    } else if (filterName === 'sigMarket') {
      return this.selectedSigMarkets.includes(selection);
    } else if (filterName === 'hq') {
      return this.selectedHqs.includes(selection);
    } else if (filterName === 'sector') {
      return this.selectedSectors.includes(selection);
    } else if (filterName === 'ranking') {
      return this.selectedRankings.includes(selection);
    } else if (filterName === 'company_type') {
      return this.selectedCompanyTypes.includes(selection);
    } else if (filterName === 'business_type') {
      return this.selectedBusinessTypes.includes(selection);
    } else if (filterName === 'supplier_category') {
      return this.selectedSupplierCategories.includes(selection);
    } else if (filterName === 'supplier') {
      return this.selectedSuppliers.includes(selection);
    } else if (filterName === 'product') {
      return this.selectedProducts.includes(selection);
    }
    return false;
  }

  get updateSelectedFilters(): any {
    this.selectedFilters = {
      region: this.selectedRegions,
      cluster: this.selectedClusters,
      sigMarket: this.selectedSigMarkets,
      hq: this.selectedHqs,
      sector: this.selectedSectors,
      ranking: this.selectedRankings,
      companyType: this.selectedCompanyTypes,
      businessType: this.selectedBusinessTypes,
      supplierCategory: this.selectedSupplierCategories,
      supplier: this.selectedSuppliers,
      product: this.selectedProducts,
    };

    // Transform the data to only include 'id' from each array
    // const idTransformFilters = this.transformDataOnlyID(updatedFilters);
    const structureTransforms = this.explorerCommonFunctions.transformToDesiredFormat(this.selectedFilters);

    // Check if all filter arrays are empty
    const allEmpty = Object.values(structureTransforms).every(
      (filter) => Array.isArray(filter) && filter.length === 0
    );

    // Return null if all filter arrays are empty, else return updated filters
    return allEmpty ? null : structureTransforms; // Cast the transformed data to SelectedFilters type
  }

  removeSelection(filterName: string, selection: any): void {
    if (filterName === 'region') {
      this.selectedRegions = this.selectedRegions.filter(
        (item) => item !== selection
      );
    } else if (filterName === 'cluster') {
      this.selectedClusters = this.selectedClusters.filter(
        (item) => item !== selection
      );
    } else if (filterName === 'sigMarket') {
      this.selectedSigMarkets = this.selectedSigMarkets.filter(
        (item) => item !== selection
      );
    } else if (filterName === 'hq') {
      this.selectedHqs = this.selectedHqs.filter((item) => item !== selection);
    } else if (filterName === 'sector') {
      this.selectedSectors = this.selectedSectors.filter(
        (item) => item !== selection
      );
    } else if (filterName === 'ranking') {
      this.selectedRankings = this.selectedRankings.filter(
        (item) => item !== selection
      );
    } else if (filterName === 'company_type') {
      this.selectedCompanyTypes = this.selectedCompanyTypes.filter(
        (item) => item !== selection
      );
    } else if (filterName === 'business_type') {
      this.selectedBusinessTypes = this.selectedBusinessTypes.filter(
        (item) => item !== selection
      );
    } else if (filterName === 'supplier_category') {
      this.selectedSupplierCategories = this.selectedSupplierCategories.filter(
        (item) => item !== selection
      );
    } else if (filterName === 'supplier') {
      this.selectedSuppliers = this.selectedSuppliers.filter(
        (item) => item !== selection
      );
    } else if (filterName === 'product') {
      this.selectedProducts = this.selectedProducts.filter(
        (item) => item !== selection
      );
    }
  }

  resetAllFilters() {
    this.selectedRegions = [];
    this.selectedClusters = [];
    this.selectedSigMarkets = [];
    this.selectedHqs = [];
    this.selectedSectors = [];
    this.selectedRankings = [];
    this.selectedCompanyTypes = [];
    this.selectedBusinessTypes = [];
    this.selectedSupplierCategories = [];
    this.selectedSuppliers = [];
    this.selectedProducts = [];
  }

  updateQueryParamsFromSelectedFilters(): void {
    const queryParams: any = {};

    // Reverse mapping: filter key ➝ query param key
    const filterKeyToQueryParam = Object.entries(this.QUERY_PARAM_TO_FILTER_KEY).reduce((acc, [param, filterKey]) => {
      acc[filterKey] = param;
      return acc;
    }, {} as Record<keyof SelectedFilters, string>);

    // Check if selectedFilters has any active values
    const hasSelectedFilters = this.selectedFilters && Object.values(this.selectedFilters).some(values => values && values.length > 0);

    if (hasSelectedFilters) {
      for (const [filterKey, values] of Object.entries(this.selectedFilters!) as [keyof SelectedFilters, any[]][]) {
        if (!values || values.length === 0) continue;

        const paramKey = filterKeyToQueryParam[filterKey];
        if (!paramKey) continue;

        queryParams[paramKey] = values
          .map(v => {
            if (typeof v === 'string' || typeof v === 'number') {
              return v;
            } else if (typeof v === 'object' && v !== null) {
              return v.id ?? v.name;
            }
            return null;
          })
          .filter(v => v !== null)
          .map(v => encodeURIComponent(v))
          .join(',');
      }
    }

    // Always include search param (empty string if undefined)
    queryParams.search = this.searchValue ?? '';

    // Include view param if selected
    if (this.selectedView) {
      queryParams.view = this.selectedView;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      replaceUrl: true,
    });
  }

  applyFilters(): void {
    this.showData = false;
    this.resultsIndex = 0;
    this.endOfResultsTemplate = false;
    this.toggleDropdown();

    if (!this.isChild) {
      this.searchValue = this.searchComponent.getCurrentInput();
    }

    const updatedFilters = this.updateSelectedFilters
    this.updateQueryParamsFromSelectedFilters();


    this.searchService.search(this.searchValue, this.resultsIndex, updatedFilters).subscribe(
      (response) => {
        this.showData = true;
        this.retailersSearchResults = response.retailer_results;
        this.vendorsSearchResults = response.vendor_results;
        this.listsSearchResults = response.list_results;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error searching RCL', error.message);
      }
    );
  }

  filterByLetter(selectedView: string, char: string, filters: SelectedFilters | null = null) {
    // Implement your filtering logic here
    this.resultsIndex = 0;
    if (char === 'ALL') {
      this.query = '';
      this.specialRoutingService.redirectToRoute({
        path: '/explore',
        queryParams: {search: ''},
      });

      this.selectedLetter = 'ALL';
      this.search(this.query, this.resultsIndex, filters);
      // this.selectView('retailers');
    } else {
      this.query = char.toUpperCase();
      this.specialRoutingService.redirectToRoute({
        path: '/explore',
        queryParams: {view: selectedView, search: char},
      });
    }
  }

  // FILTER  FUNCTIONS END -----


  // SEARCH FUNCTIONS START -----
  search(query: string, resultsIndex: number | null = 0, filters: SelectedFilters | null = null): void {
    this.showData = false;
    this.endOfResultsTemplate = false;
    this.resultsIndex = 0;
    this.totalHits = 0;
    this.toggleDropdown();
    this.dropdownVisible = false;
    this.listsSearchResults = []; // Clear previous list search results
    this.retailersSearchResults = []; // Clear previous retailer search results
    this.vendorsSearchResults = [];

    // Update the query parameters without reloading
    if (!this.isChild) {
      // Get current query params first
      const currentParams = { ...this.route.snapshot.queryParams };

      // Update the 'search' param with the new query value
      currentParams['search'] = query;

      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: currentParams,
        queryParamsHandling: 'merge', // merge instead of replace
      });
    }

    this.searchService.search(query, resultsIndex, filters).subscribe(
      (response) => {
        console.log(response)

        this.showData = true;
        this.retailersSearchResults = response.retailer_results;
        this.vendorsSearchResults = response.vendor_results;
        this.listsSearchResults = response.list_results;

        if ((this.selectedView && this.selectedView === 'retailers' && this.retailersSearchResults.length === 0) ||
          (this.selectedView && this.selectedView === 'suppliers' && this.vendorsSearchResults.length === 0) ||
          (this.selectedView && this.selectedView === 'lists' && this.listsSearchResults.length === 0)) {
          this.selectView('all');

        }

        this.cdr.detectChanges(); // Manually trigger change detection
      },
      (error: any) => {
        console.error('Error searching RCL', error.message);
      }
    );
  }

  // SEARCH FUNCTIONS END -----


  // CLICK/DISABLE FUNCTIONS START -----
  cardDisabled(cardType: string, result: any) {
    if (cardType === 'retailer') {
      if (!this.auth.isPremiumUser() && !this.auth.isEssentialUser()) {

        return this.allClustersUnavailable(result.retailer.clusters)
      } else {
        return false
      }
    } else if (cardType === 'vendor') {
      return false
    } else if (cardType === 'list') {
      if (!this.auth.isPremiumUser() && !this.auth.isEssentialUser()) { //Basic
        if (result.list.frontend_name_str.includes('UK')) {
          return false;
        } else {
          return true;
        }
      } else { //Non basic
        return false
      }
    }
    return true

  }

  allClustersUnavailable(clusters: { available_to_basic: boolean }[]): boolean {
    return clusters.every((cluster) => !cluster.available_to_basic);
  }

  logInfoAndReroute(result: any, type: any): void {
    this.showData = false;
    this.showInnerLoader = false;
    if (type === 'list') {
      // Use optional chaining to safely access properties
      const slug = result.list?.slug; // Use optional chaining
      const id = result.list?.rcl_id; // Use optional chaining
      const compare = result.list?.relevant_compare
      

      // Check if slug and id are defined before proceeding
      if (slug && id) {
        const route = this.specialRoutingService.prepareRoute(
          '/profile',
          slug,
          id,
          null,
          compare
        );
        
        this.specialRoutingService.redirectToRoute(route);
      } else {
        console.error('Slug or ID is missing for list type.');
      }
    } else if (type === 'retailer') {
      // Use optional chaining to safely access properties
      const slug = result.retailer?.rcl?.slug; // Use optional chaining
      const id = result.retailer?.rcl?.id; // Use optional chaining
      const compare = [result.retailer?.sector.base_rcl_id]
      // Check if slug and id are defined before proceeding
      if (slug && id) {
        const route = this.specialRoutingService.prepareRoute(
          '/profile',
          slug,
          id,
          null,
          compare
        );
        this.specialRoutingService.redirectToRoute(route);
      } else {
        console.error('Slug or ID is missing for retailer type.');
      }
    } else if (type === 'vendor') {
      // Use optional chaining to safely access properties
      const slug = result.vendor?.rcl?.slug; // Use optional chaining
      const id = result.vendor?.rcl?.id; // Use optional chaining


      // Check if slug and id are defined before proceeding
      if (slug && id) {
        const route = this.specialRoutingService.prepareRoute(
          '/profile',
          slug,
          id,
        );
        this.specialRoutingService.redirectToRoute(route);
      } else {
        console.error('Slug, Echelon or ID is missing for vendor type.');
      }
    } else {
      console.error('Reroute type is unknown.');
    }
  }

  createClickHandler(result: any, type: any): () => void {

    return () => this.logInfoAndReroute(result, type);
  }

  // CLICK/DISABLE FUNCTIONS END -----

  //  TABULAR DOWNLOAD FUNCTIONS START -----
  toggleDownloadDialog() {
    this.showDownloadDialog = !this.showDownloadDialog;
  }

  toggleSupplierDownloadDialog() {
    this.showSupplierDownloadDialog = !this.showSupplierDownloadDialog;
  }

  async onDownloadClick(contentType: string, event: MouseEvent) {

    if (contentType === 'multi-retailer') {


      this.loadingRetailersDownload.value = true

      const data = {
        query: this.query,
        filters: this.updateSelectedFilters,
      };
      if (contentType !== null && data.filters !== null) {

        try {
          await this.tabularDataDownloadService.updateAvailableEchelons(
            this.rclIDs,
            this.availableEchelons,
            data,
            contentType as string
          );
        } catch (error) {
          console.error('Failed to load echelons', error);

          this.loadingRetailersDownload.value = false;
        } finally {
            this.toggleDownloadDialog();
        }

      }
    }

    else if (contentType === 'multi-supplier') {


      this.loadingSuppliersDownload.value = true;

      const data = {
        query: this.query,
        filters: this.updateSelectedFilters,
      };
      if (contentType !== null && data.filters !== null) {

        try {
          await this.tabularDataDownloadService.updateAvailableEchelons(
            this.rclIDs,
            this.availableEchelons,
            data,
            contentType as string
          );
        } catch (error) {
          console.error('Failed to load echelons', error);
          this.loadingSuppliersDownload.value = false;
        } finally {
            this.toggleSupplierDownloadDialog();
        }

      }
    }

    event.stopPropagation();
  }

  onToggleStateChanged(contentType:string, event: { value: boolean }) {
    if(contentType === 'multi-retailer') {
      this.loadingRetailersDownload.value = event.value;
    }
    else if (contentType === 'multi-supplier') {
      this.loadingSuppliersDownload.value = event.value;
    }

  }
  //  TABULAR DOWNLOAD FUNCTIONS END -----

}
